'use client';

import { useMediaQuery } from '@/hooks/dom';
import { mediaQueries } from '@/theme/breakpoints';
import {
  MOBILE_CONTROLS_SCALE,
} from './Cmarkdownmindmap.constants';
import {
  getMindmapRenderModel,
} from './mindmap-render-model';
import { useCmarkdownmindmapInteractionState } from './useCmarkdownmindmapInteractionState';
import { useCmarkdownmindmapParsedModel } from './useCmarkdownmindmapParsedModel';
import { useCmarkdownmindmapRuntime } from './useCmarkdownmindmapRuntime';
import { useMindmapEntranceState } from './useMindmapEntranceState';
import { useMindmapExpansionState } from './useMindmapExpansionState';
import { useMindmapGraphModel } from './useMindmapGraphModel';
import { useMindmapKeyboardNavigation } from './useMindmapKeyboardNavigation';
import { useMindmapViewportFit } from './useMindmapViewportFit';
import { useMindmapZoomControls } from './useMindmapZoomControls';
import type { DataElScope } from '@/utils/dataEl';

interface UseCmarkdownmindmapControllerInput {
  content: string;
  dataElScope: DataElScope;
}

export function useCmarkdownmindmapController({
  content,
  dataElScope,
}: UseCmarkdownmindmapControllerInput) {
  const isMobile = useMediaQuery(mediaQueries.mobile);
  const parsedModel = useCmarkdownmindmapParsedModel({ content, dataElScope });
  const runtime = useCmarkdownmindmapRuntime({ parsed: parsedModel.parsed });
  const expansion = useMindmapExpansionState({
    parsed: parsedModel.parsed,
    pendingFitRef: runtime.pendingFitRef,
  });
  const graphModel = useMindmapGraphModel({
    expandedNodeIds: expansion.expandedNodeIds,
    hoveredNodeId: runtime.hoveredNodeId,
    parsed: parsedModel.parsed,
  });
  const isEntranceComplete = useMindmapEntranceState({
    content,
    didInitialFitRef: runtime.didInitialFitRef,
    parsed: parsedModel.parsed,
    pendingFitRef: runtime.pendingFitRef,
    setHoveredNodeId: runtime.setHoveredNodeId,
    setSelectedMindmapNode: runtime.setSelectedMindmapNode,
  });

  useMindmapViewportFit({
    didInitialFitRef: runtime.didInitialFitRef,
    layout: graphModel.layout,
    parentMap: graphModel.parentMap,
    parsed: parsedModel.parsed,
    pendingFitRef: runtime.pendingFitRef,
    reactFlowInstance: runtime.reactFlowInstance,
    reactFlowWrapperRef: runtime.reactFlowWrapperRef,
    selectedNodeId: runtime.selectedNodeId,
    setSelectedMindmapNode: runtime.setSelectedMindmapNode,
    shouldFitSelectedNodeRef: runtime.shouldFitSelectedNodeRef,
    visibleNodeIds: graphModel.visibleNodeIds,
  });

  const zoomControls = useMindmapZoomControls({
    reactFlowInstance: runtime.reactFlowInstance,
    setZoomLevel: runtime.setZoomLevel,
  });
  const keyboardNavigation = useMindmapKeyboardNavigation({
    expandedNodeIds: expansion.expandedNodeIds,
    parentMap: graphModel.parentMap,
    parsed: parsedModel.parsed,
    reactFlowInstance: runtime.reactFlowInstance,
    selectedNodeId: runtime.selectedNodeId,
    setSelectedMindmapNode: runtime.setSelectedMindmapNode,
    updateNodeExpansion: expansion.updateNodeExpansion,
    visibleNodeIds: graphModel.visibleNodeIds,
    visibleNodeYById: graphModel.visibleNodeYById,
  });
  const interactionState = useCmarkdownmindmapInteractionState({
    highlightedNodeIds: graphModel.highlightedNodeIds,
    hoveredNodeId: runtime.hoveredNodeId,
    isEntranceComplete,
    onToggleExpand: expansion.onToggleExpand,
    selectedNodeId: runtime.selectedNodeId,
    setSelectedMindmapNode: runtime.setSelectedMindmapNode,
  });

  return {
    controlsScaleStyle: isMobile
      ? { transform: `scale(${MOBILE_CONTROLS_SCALE})` }
      : undefined,
    expansion,
    interactionState,
    keyboardNavigation,
    parsedModel,
    renderModel: getMindmapRenderModel({
      layout: graphModel.layout,
      parsed: parsedModel.parsed,
    }),
    runtime,
    zoomControls,
  };
}
